/**
 * POS AI API
 * Frontend API calls for AI-powered POS features
 * 
 * OFFLINE HANDLING:
 * - AI features are optional enhancements
 * - When offline, they gracefully return empty/fallback data
 * - No queuing of AI requests (not critical for offline operation)
 * - Components should handle { success: false, offline: true } responses
 */

import axios from 'axios';
import { HEADERS } from '../../../../../API/UrlAndPaths';

/**
 * Check if we're offline
 */
const isOffline = () => !navigator.onLine;

/**
 * Create offline response (graceful degradation)
 */
const offlineResponse = (message = 'AI features unavailable offline') => ({
  success: false,
  offline: true,
  message,
  data: null
});

/**
 * Wrap API call with offline handling and timeout
 * AI calls should fail fast and not block the UI
 */
const safeAICall = async (apiCall, fallbackMessage) => {
  // If offline, return immediately without trying
  if (isOffline()) {
    console.log('📴 AI API skipped - offline');
    return offlineResponse(fallbackMessage);
  }
  
  try {
    // Add timeout for AI calls (10 seconds max)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    const result = await apiCall(controller.signal);
    clearTimeout(timeoutId);
    return result;
  } catch (error) {
    // Handle network errors gracefully
    if (error.name === 'AbortError') {
      console.warn('⏱️ AI API timeout');
      return { success: false, message: 'AI request timed out', data: null };
    }
    
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      console.warn('📴 AI API network error - treating as offline');
      return offlineResponse(fallbackMessage);
    }
    
    // For other errors, return graceful failure
    console.error('AI API error:', error.message);
    return { 
      success: false, 
      message: error.response?.data?.message || error.message || 'AI service unavailable',
      data: null 
    };
  }
};

/**
 * Get smart product recommendations based on cart items
 * @param {Object} params - { cartItems, customerId, orderType }
 */
export const getRecommendationsAPI = async (params) => {
  return safeAICall(
    async (signal) => {
      const response = await axios.post("api/pos-ai/recommendations", params, { 
        ...HEADERS, 
        signal,
        skipOfflineCheck: true // Don't queue this request
      });
      return response.data;
    },
    'Recommendations unavailable offline'
  );
};

/**
 * Get AI-powered recommendations using Gemini
 * @param {Object} params - { cartItems, customerId, orderType, timeOfDay }
 */
export const getAiRecommendationsAPI = async (params) => {
  return safeAICall(
    async (signal) => {
      const response = await axios.post("api/pos-ai/ai-recommendations", params, { 
        ...HEADERS, 
        signal,
        skipOfflineCheck: true
      });
      return response.data;
    },
    'AI recommendations unavailable offline'
  );
};

/**
 * Parse voice input to identify products
 * @param {Object} params - { transcript }
 */
export const parseVoiceAPI = async (params) => {
  return safeAICall(
    async (signal) => {
      const response = await axios.post("api/pos-ai/parse-voice", params, { 
        ...HEADERS, 
        signal,
        skipOfflineCheck: true
      });
      return response.data;
    },
    'Voice parsing unavailable offline'
  );
};

/**
 * Get customer intelligence data
 * @param {Object} params - { customerId }
 */
export const getCustomerIntelligenceAPI = async (params) => {
  return safeAICall(
    async (signal) => {
      const response = await axios.post("api/pos-ai/customer-intelligence", params, { 
        ...HEADERS, 
        signal,
        skipOfflineCheck: true
      });
      return response.data;
    },
    'Customer intelligence unavailable offline'
  );
};

/**
 * Quick customer lookup by phone number
 * @param {Object} params - { phoneNumber }
 */
export const quickCustomerLookupAPI = async (params) => {
  return safeAICall(
    async (signal) => {
      const response = await axios.post("api/pos-ai/quick-customer-lookup", params, { 
        ...HEADERS, 
        signal,
        skipOfflineCheck: true
      });
      return response.data;
    },
    'Customer lookup unavailable offline'
  );
};

/**
 * Get real-time sales insights
 */
export const getSalesInsightsAPI = async () => {
  return safeAICall(
    async (signal) => {
      const response = await axios.get("api/pos-ai/sales-insights", { 
        ...HEADERS, 
        signal,
        skipOfflineCheck: true
      });
      return response.data;
    },
    'Sales insights unavailable offline'
  );
};
